import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Grid, Card, CardContent, Typography, Box, Chip, Divider,
  Button, CircularProgress, Avatar,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import BusinessIcon from '@mui/icons-material/Business';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import Layout from '../../components/Layout/Layout';
import PageHeader from '../../components/Common/PageHeader';
import { getBookDetailApi } from '../../api/books';
import { MEDIA_URL } from '../../utils/constants';

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 150, color: 'text.secondary' }}>{label}:</Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}

export default function BookDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getBookDetailApi(id).then((r) => setBook(r.data)).catch(() => setBook(null)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <Layout title="Book Details"><Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box></Layout>;
  if (!book) return <Layout title="Book Details"><Typography>Book not found.</Typography></Layout>;

  const publisher = book.publisher || {};

  return (
    <Layout title="Book Details">
      <PageHeader
        title="Book Details"
        breadcrumbs={[{ label: 'Books', path: '/books' }, { label: book.title }]}
        action={<Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/books')}>Back to Books</Button>}
      />
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card sx={{ position: 'sticky', top: 80 }}>
            {book.cover_image ? (
              <Box component="img" src={`${MEDIA_URL}${book.cover_image}`} alt={book.title}
                sx={{ width: '100%', maxHeight: 360, objectFit: 'cover' }} />
            ) : (
              <Box sx={{ height: 280, bgcolor: 'primary.light', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <MenuBookIcon sx={{ fontSize: 100, color: 'white', opacity: 0.7 }} />
              </Box>
            )}
            <CardContent>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>{book.title}</Typography>
              <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 1 }}>by {book.author_name}</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                <Chip label={book.genre} color="primary" size="small" />
                {book.language && <Chip label={book.language} size="small" variant="outlined" />}
                {book.target_segment && <Chip label={book.target_segment} size="small" variant="outlined" />}
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>₹{book.price}</Typography>
              <Typography variant="body2" color="text.secondary">
                Contact publisher directly to purchase this book (no online payment).
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Book Information</Typography>
              <Divider sx={{ mb: 2 }} />
              <InfoRow label="ISBN" value={book.isbn} />
              <InfoRow label="Author" value={book.author_name} />
              <InfoRow label="Genre / Category" value={book.genre} />
              <InfoRow label="Language" value={book.language} />
              <InfoRow label="Target Segment" value={book.target_segment} />
              <InfoRow label="Number of Pages" value={book.pages ? `${book.pages} pages` : null} />
              <InfoRow label="Publication Date" value={book.publication_date} />
              <InfoRow label="ISBN" value={book.isbn} />
            </CardContent>
          </Card>

          {book.description && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Synopsis</Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Typography variant="body1" sx={{ lineHeight: 1.8, whiteSpace: 'pre-line' }}>
                  {book.description}
                </Typography>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BusinessIcon color="primary" /> Publisher Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                  {publisher.name?.[0]?.toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{publisher.name}</Typography>
                  {publisher.company_name && (
                    <Typography variant="body2" color="text.secondary">{publisher.company_name}</Typography>
                  )}
                  {publisher.email && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                      <EmailIcon fontSize="small" color="action" />
                      <Typography variant="body2">{publisher.email}</Typography>
                    </Box>
                  )}
                  {publisher.mobile_number && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                      <PhoneIcon fontSize="small" color="action" />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{publisher.mobile_number}</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
              <Box sx={{ mt: 2, p: 1.5, bgcolor: 'info.lighter', borderRadius: 1, border: '1px solid', borderColor: 'info.light' }}>
                <Typography variant="body2" color="info.dark">
                  To purchase this book, contact the publisher directly via phone or email.
                  No online ordering is available.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Layout>
  );
}
